import{lifeFamilyFor,type LifeFamily}from'./lifeOmega'
import type{AttackKind}from'./simulation'
import type{BodyPlan}from'./motionField'
import{CHARACTER_DNA_CONTRIBUTIONS}from'./characterDNAContributions'
import{normalizeFightHeroId}from'./battleArenaFightRoster'

export type PowerSource='physical'|'elemental'|'rift'|'void'|'arcane'|'celestial'|'bioelectric'|'crystal'|'temporal'|'hybrid'
export type CombatArchetype='duelist'|'bruiser'|'colossus'|'controller'|'predator'|'guardian'|'aerial'|'trickster'|'adaptive'
export type DriveProfile={survival:number;dominance:number;protection:number;curiosity:number;mastery:number;restraint:number}
export type CognitiveArchitectureGenome={
 conscious:true
 selfModel:number
 volition:number
 workingMemory:number
 planning:number
 metacognition:number
 socialCognition:number
 goalPersistence:number
 impulseControl:number
 emotionalIntegration:number
 attentionControl:number
 episodicMemory:number
 semanticMemory:number
 imagination:number
 causalReasoning:number
 spatialReasoning:number
 temporalReasoning:number
 theoryOfMind:number
 languageCapacity:number
 sensorFusion:number
}
export type PhysiologyGenome={
 stature:number;mass:number;reach:number;durability:number;regeneration:number;neuralSpeed:number;
 balance:number;elasticity:number;energyEfficiency:number;oxygenReliance:number;structuralStability:number
}
export type SensoryGenome={
 vision:number;hearing:number;olfaction:number;gustation:number;proprioception:number;vestibular:number;tactile:number;
 thermalSense:number;chemicalSense:number;electromagneticSense:number;echolocation:number;vibrationSense:number;
 painSensitivity:number;interoception:number;energeticSense:number;spatialSense:number
}
export type MindGenome={
 aggression:number;caution:number;discipline:number;patience:number;creativity:number;prediction:number;
 courage:number;adaptability:number;aeriality:number;precision:number;learning:number;inhibition:number;drives:DriveProfile;architecture:CognitiveArchitectureGenome
}
export type CombatGenome={
 preferredRange:number;tempo:number;pressure:number;counterBias:number;guardBias:number;throwBias:number;
 specialBias:number;resourceConservation:number;comboPersistence:number;finisherDiscipline:number;
 feintBias:number;environmentUse:number;pursuit:number;retreatDiscipline:number;aerialMastery:number
}
export type QuantumVacuumGenome={
 coupling:number
 coherence:number
 stability:number
 extraction:number
 phaseControl:number
 entropyTolerance:number
 shielding:number
 perception:number
}
export type PowerGenome={
 source:PowerSource;amplitude:number;control:number;volatility:number;recovery:number;precision:number;
 area:number;transformation:number;channeling:number;environmentCoupling:number;quantumVacuum:QuantumVacuumGenome
}
export type ExpressionGenome={
 motionWeight:number;cadence:number;asymmetry:number;grace:number;ferocity:number;restraint:number;
 telegraph:number;aura:number;cameraPresence:number;audioPresence:number;secondaryMotion:number
}
export type LearningGenome={
 memoryDepth:number;learningRate:number;forgettingRate:number;opponentSpecificity:number;
 noveltyDrive:number;habitStrength:number;adaptationCeiling:number
}
export type CapabilityGenome={
 flight:boolean;quantum:boolean;transformation:boolean;regeneration:boolean;environmentManipulation:boolean;
 attacks:Record<AttackKind,number>
}
export type CharacterDNALinks={
 biologicalDnaId:string|null
 animationDnaId:string|null
 rigDnaId:string|null
 attackPatternId:string|null
 specialAbilityId:string|null
 ultimateAbilityId:string|null
 aiBehaviorId:string|null
 elementalId:string|null
 weaponId:string|null
 audioProfileId:string|null
 cameraProfileId:string|null
 skillPresentationProfileId:string|null
 combatTuningProfileId:string|null
 destructionProfileId:string|null
 modularAssemblyProfileId:string|null
 modelAssetId:string|null
 sourceRegistry:string|null
}
export type CharacterDNA={
 fighterId:string;fingerprint:string;lineage:LifeFamily;bodyPlan:BodyPlan;archetype:CombatArchetype;
 physiology:PhysiologyGenome;senses:SensoryGenome;mind:MindGenome;combat:CombatGenome;power:PowerGenome;
 expression:ExpressionGenome;learning:LearningGenome;capabilities:CapabilityGenome;links:CharacterDNALinks
}
export type CharacterDNAPatch={
 lineage?:LifeFamily;bodyPlan?:BodyPlan;archetype?:CombatArchetype
 physiology?:Partial<PhysiologyGenome>;senses?:Partial<SensoryGenome>
 mind?:Partial<Omit<MindGenome,'drives'|'architecture'>> & {drives?:Partial<DriveProfile>;architecture?:Partial<CognitiveArchitectureGenome>}
 combat?:Partial<CombatGenome>;power?:Partial<Omit<PowerGenome,'quantumVacuum'>> & {quantumVacuum?:Partial<QuantumVacuumGenome>};expression?:Partial<ExpressionGenome>;learning?:Partial<LearningGenome>
 capabilities?:Partial<Omit<CapabilityGenome,'attacks'>> & {attacks?:Partial<Record<AttackKind,number>>}
 links?:Partial<CharacterDNALinks>
}
export type CharacterDNAContribution={
 id:string;source:string;fighterId:string;priority:number;patch:CharacterDNAPatch
}

const clamp=(v:number,lo=0,hi=1)=>Math.max(lo,Math.min(hi,Number.isFinite(v)?v:lo))
function hash32(input:string){let h=2166136261>>>0;for(let i=0;i<input.length;i++){h^=input.charCodeAt(i);h=Math.imul(h,16777619)}h^=h>>>16;h=Math.imul(h,0x7feb352d);h^=h>>>15;h=Math.imul(h,0x846ca68b);h^=h>>>16;return h>>>0}
const unit=(id:string,salt:string)=>hash32(id+'|'+salt)/4294967295
const gene=(id:string,salt:string,min=.18,max=.88)=>min+unit(id,salt)*(max-min)
const round=(v:number)=>Math.round(clamp(v)*10000)/10000
const tune=<T,K extends keyof T>(source:T,key:K,delta:number)=>({...source,[key]:clamp(Number(source[key])+delta)}) as T

function bodyPlanFor(id:string,family:LifeFamily):BodyPlan{
 const n=id.toLowerCase()
 if(/tita|coloss|mountain/.test(n))return'colossus'
 if(/dragon|dragao|phoenix|fenix|seraph|angel|wisp/.test(n))return'flying'
 if(/serpent|leviathan|wyrm/.test(n))return'serpentine'
 if(/beast|wolf|lion|panther|hound/.test(n))return'quadruped'
 if(/golem|synthetic|mech|construct/.test(n))return'synthetic-organic'
 if(family==='tidal')return'aquatic'
 if(family==='wisp')return'amorphous'
 return'humanoid'
}
function archetypeFor(id:string,plan:BodyPlan):CombatArchetype{
 const n=id.toLowerCase()
 if(plan==='colossus'||/golem|tita/.test(n))return'colossus'
 if(/guardian|warden|sentinel|bulwark/.test(n))return'guardian'
 if(plan==='flying')return'aerial'
 if(/assassin|reaper|shadow/.test(n))return'predator'
 if(/mage|oracle|time|quantum|controller/.test(n))return'controller'
 if(/trick|rogue|jester/.test(n))return'trickster'
 if(/brute|vulcao|berserk/.test(n))return'bruiser'
 if(/elyon|duel|sword/.test(n))return'duelist'
 return'adaptive'
}
function powerSourceFor(id:string,family:LifeFamily):PowerSource{
 const n=id.toLowerCase()
 if(/time|chrono|quantum/.test(n))return'temporal'
 if(/elyon|rift/.test(n))return'rift'
 if(/void|shadow/.test(n))return'void'
 if(/fenix|vulcao|flame|fire|tide|storm|ice/.test(n))return'elemental'
 if(/crystal|prism/.test(n))return'crystal'
 if(/angel|seraph|celestial/.test(n))return'celestial'
 if(/mage|arcane|spell/.test(n))return'arcane'
 if(family==='xeno'||/alien/.test(n))return'bioelectric'
 return'physical'
}
function capabilitiesFor(id:string,plan:BodyPlan,source:PowerSource):CapabilityGenome{
 const n=id.toLowerCase(),flight=plan==='flying'||/fly|aerial/.test(n)
 return{
  flight,quantum:/quantum|time|rift|elyon/.test(n),transformation:/jhadson|fenix|plague|shifter/.test(n),
  regeneration:/fenix|revenant|plague|wisp/.test(n),environmentManipulation:source!=='physical',
  attacks:{light:round(gene(id,'cap-light',.7,1)),heavy:round(gene(id,'cap-heavy',.62,1)),special:round(gene(id,'cap-special',.68,1)),throw:round(gene(id,'cap-throw',.48,.95))}
 }
}

function baseline(id:string,lineage:LifeFamily,plan:BodyPlan,archetype:CombatArchetype,source:PowerSource):CharacterDNA{
 let physiology:PhysiologyGenome={
  stature:gene(id,'stature'),mass:gene(id,'mass'),reach:gene(id,'reach'),durability:gene(id,'durability'),
  regeneration:gene(id,'regeneration'),neuralSpeed:gene(id,'neural-speed'),balance:gene(id,'balance'),
  elasticity:gene(id,'elasticity'),energyEfficiency:gene(id,'energy-eff'),oxygenReliance:gene(id,'oxygen'),
  structuralStability:gene(id,'stability')
 }
 let senses:SensoryGenome={
  vision:gene(id,'vision'),hearing:gene(id,'hearing'),olfaction:gene(id,'olfaction'),gustation:gene(id,'gustation'),
  proprioception:gene(id,'proprioception'),vestibular:gene(id,'vestibular'),tactile:gene(id,'tactile'),
  thermalSense:gene(id,'thermal-sense'),chemicalSense:gene(id,'chemical-sense'),
  electromagneticSense:gene(id,'electromagnetic-sense'),echolocation:gene(id,'echolocation',.04,.78),
  vibrationSense:gene(id,'vibration-sense'),painSensitivity:gene(id,'pain'),
  interoception:gene(id,'interoception'),energeticSense:gene(id,'energy-sense'),spatialSense:gene(id,'spatial')
 }
 let mind:MindGenome={
  aggression:gene(id,'aggression'),caution:gene(id,'caution'),discipline:gene(id,'discipline'),
  patience:gene(id,'patience'),creativity:gene(id,'creativity'),prediction:gene(id,'prediction'),
  courage:gene(id,'courage'),adaptability:gene(id,'adaptability'),aeriality:gene(id,'aeriality'),
  precision:gene(id,'precision'),learning:gene(id,'learning'),inhibition:gene(id,'inhibition'),
  drives:{survival:gene(id,'drive-survival'),dominance:gene(id,'drive-dominance'),protection:gene(id,'drive-protection'),curiosity:gene(id,'drive-curiosity'),mastery:gene(id,'drive-mastery'),restraint:gene(id,'drive-restraint')},
  architecture:{
   conscious:true,
   selfModel:gene(id,'brain-self-model',.45,.92),
   volition:gene(id,'brain-volition',.5,.95),
   workingMemory:gene(id,'brain-working-memory',.4,.9),
   planning:gene(id,'brain-planning',.35,.92),
   metacognition:gene(id,'brain-metacognition',.3,.9),
   socialCognition:gene(id,'brain-social',.32,.9),
   goalPersistence:gene(id,'brain-goal-persistence',.4,.95),
   impulseControl:gene(id,'brain-impulse-control',.28,.9),
   emotionalIntegration:gene(id,'brain-emotional-integration',.3,.92),
   attentionControl:gene(id,'brain-attention-control',.28,.94),
   episodicMemory:gene(id,'brain-episodic-memory',.24,.94),
   semanticMemory:gene(id,'brain-semantic-memory',.18,.94),
   imagination:gene(id,'brain-imagination',.12,.94),
   causalReasoning:gene(id,'brain-causal-reasoning',.16,.94),
   spatialReasoning:gene(id,'brain-spatial-reasoning',.2,.96),
   temporalReasoning:gene(id,'brain-temporal-reasoning',.12,.94),
   theoryOfMind:gene(id,'brain-theory-of-mind',.08,.92),
   languageCapacity:gene(id,'brain-language-capacity',.04,.94),
   sensorFusion:gene(id,'brain-sensor-fusion',.22,.96),
  }
 }
 let combat:CombatGenome={
  preferredRange:gene(id,'range'),tempo:gene(id,'tempo'),pressure:gene(id,'pressure'),counterBias:gene(id,'counter'),
  guardBias:gene(id,'guard'),throwBias:gene(id,'throw'),specialBias:gene(id,'special'),resourceConservation:gene(id,'conserve'),
  comboPersistence:gene(id,'combo'),finisherDiscipline:gene(id,'finisher'),feintBias:gene(id,'feint'),
  environmentUse:gene(id,'environment'),pursuit:gene(id,'pursuit'),retreatDiscipline:gene(id,'retreat'),aerialMastery:gene(id,'air-mastery')
 }
 let power:PowerGenome={
  source,amplitude:gene(id,'power-amplitude'),control:gene(id,'power-control'),volatility:gene(id,'power-volatility'),
  recovery:gene(id,'power-recovery'),precision:gene(id,'power-precision'),area:gene(id,'power-area'),
  transformation:gene(id,'power-transform'),channeling:gene(id,'power-channel'),environmentCoupling:gene(id,'power-environment'),
  quantumVacuum:{
   coupling:gene(id,'vacuum-coupling',.02,.24),
   coherence:gene(id,'vacuum-coherence',.04,.28),
   stability:gene(id,'vacuum-stability',.08,.34),
   extraction:gene(id,'vacuum-extraction',.02,.22),
   phaseControl:gene(id,'vacuum-phase-control',.04,.3),
   entropyTolerance:gene(id,'vacuum-entropy-tolerance',.08,.36),
   shielding:gene(id,'vacuum-shielding',.04,.3),
   perception:gene(id,'vacuum-perception',.03,.28),
  }
 }
 let expression:ExpressionGenome={
  motionWeight:gene(id,'motion-weight'),cadence:gene(id,'cadence'),asymmetry:gene(id,'asymmetry'),
  grace:gene(id,'grace'),ferocity:gene(id,'ferocity'),restraint:gene(id,'expression-restraint'),
  telegraph:gene(id,'telegraph'),aura:gene(id,'aura'),cameraPresence:gene(id,'camera'),
  audioPresence:gene(id,'audio'),secondaryMotion:gene(id,'secondary')
 }
 let learning:LearningGenome={
  memoryDepth:gene(id,'memory'),learningRate:gene(id,'learn-rate'),forgettingRate:gene(id,'forget'),
  opponentSpecificity:gene(id,'opponent-specific'),noveltyDrive:gene(id,'novelty'),habitStrength:gene(id,'habit'),
  adaptationCeiling:gene(id,'adapt-ceiling')
 }
 if(plan==='colossus'){physiology=tune(tune(tune(physiology,'mass',.32),'durability',.24),'neuralSpeed',-.2);mind=tune(tune(mind,'patience',.18),'aeriality',-.38);combat=tune(tune(combat,'pressure',.18),'tempo',-.22);expression=tune(tune(expression,'motionWeight',.3),'cameraPresence',.2)}
 if(plan==='flying'){physiology=tune(tune(physiology,'balance',.18),'mass',-.16);senses=tune(tune(tune(senses,'vestibular',.28),'spatialSense',.24),'vision',.12);mind=tune(mind,'aeriality',.38);combat=tune(combat,'aerialMastery',.35);expression=tune(expression,'secondaryMotion',.24)}
 if(archetype==='guardian'){mind=tune(tune(tune(mind,'discipline',.22),'caution',.16),'inhibition',.14);combat=tune(tune(combat,'guardBias',.3),'counterBias',.16)}
 if(archetype==='predator'){mind=tune(tune(tune(mind,'prediction',.2),'aggression',.15),'patience',.08);combat=tune(tune(combat,'pursuit',.22),'finisherDiscipline',.18)}
 if(archetype==='controller'){mind=tune(tune(tune(mind,'creativity',.22),'prediction',.18),'adaptability',.18);combat=tune(tune(combat,'environmentUse',.28),'specialBias',.18);power=tune(tune(power,'control',.2),'precision',.16)}
 if(archetype==='bruiser'){physiology=tune(tune(physiology,'durability',.18),'mass',.14);mind=tune(mind,'aggression',.2);combat=tune(tune(combat,'pressure',.25),'retreatDiscipline',-.18)}
 if(source==='temporal'||source==='rift'){senses=tune(tune(senses,'energeticSense',.2),'electromagneticSense',.1);power=tune(tune(power,'control',.15),'volatility',.08)}
 if(source==='void'){senses=tune(tune(senses,'energeticSense',.14),'electromagneticSense',.12);mind=tune(tune(mind,'prediction',.12),'inhibition',-.08);power=tune(power,'volatility',.18)}
 if(source==='elemental'){senses=tune(senses,'thermalSense',.12);power=tune(tune(power,'amplitude',.16),'environmentCoupling',.24)}
 if(source==='crystal'){physiology=tune(physiology,'structuralStability',.22);senses=tune(senses,'vibrationSense',.12);power=tune(tune(power,'precision',.16),'area',.1)}
 if(plan==='aquatic'){senses=tune(tune(tune(senses,'chemicalSense',.22),'vibrationSense',.18),'hearing',.12)}
 if(plan==='serpentine'||plan==='quadruped'){senses=tune(tune(senses,'olfaction',.22),'vibrationSense',.14)}
 if(plan==='amorphous'){senses=tune(tune(tune(senses,'chemicalSense',.24),'electromagneticSense',.16),'tactile',.16)}
 const capabilities=capabilitiesFor(id,plan,source)
 if(capabilities.quantum){
  const sourceGain=source==='temporal'?.18:source==='rift'?.14:source==='void'?.1:.08
  power={...power,quantumVacuum:{
   coupling:clamp(power.quantumVacuum.coupling+.34+sourceGain),
   coherence:clamp(power.quantumVacuum.coherence+.3+sourceGain),
   stability:clamp(power.quantumVacuum.stability+.28+sourceGain*.7),
   extraction:clamp(power.quantumVacuum.extraction+.24+sourceGain*.65),
   phaseControl:clamp(power.quantumVacuum.phaseControl+.32+sourceGain),
   entropyTolerance:clamp(power.quantumVacuum.entropyTolerance+.26+sourceGain*.7),
   shielding:clamp(power.quantumVacuum.shielding+.2+sourceGain*.55),
   perception:clamp(power.quantumVacuum.perception+.3+sourceGain*.85),
  }}
  senses={
   ...senses,
   energeticSense:clamp(senses.energeticSense+.12),
   electromagneticSense:clamp(senses.electromagneticSense+.1),
  }
  mind={...mind,architecture:{
   ...mind.architecture,
   temporalReasoning:clamp(mind.architecture.temporalReasoning+.12),
   causalReasoning:clamp(mind.architecture.causalReasoning+.08),
   sensorFusion:clamp(mind.architecture.sensorFusion+.08),
  }}
 }
 return{fighterId:id,fingerprint:'',lineage,bodyPlan:plan,archetype,physiology,senses,mind,combat,power,expression,learning,capabilities,links:{biologicalDnaId:id,animationDnaId:null,rigDnaId:id,attackPatternId:null,specialAbilityId:null,ultimateAbilityId:null,aiBehaviorId:null,elementalId:null,weaponId:null,audioProfileId:null,cameraProfileId:null,skillPresentationProfileId:null,combatTuningProfileId:null,destructionProfileId:null,modularAssemblyProfileId:null,modelAssetId:null,sourceRegistry:'fight'}}
}

function applyContribution(base:CharacterDNA,contribution:CharacterDNAContribution):CharacterDNA{
 const patch=contribution.patch
 return{
  ...base,
  lineage:patch.lineage??base.lineage,
  bodyPlan:patch.bodyPlan??base.bodyPlan,
  archetype:patch.archetype??base.archetype,
  physiology:{...base.physiology,...patch.physiology},
  senses:{...base.senses,...patch.senses},
  mind:{...base.mind,...patch.mind,drives:{...base.mind.drives,...patch.mind?.drives},architecture:{...base.mind.architecture,...patch.mind?.architecture,conscious:true}},
  combat:{...base.combat,...patch.combat},
  power:{...base.power,...patch.power,quantumVacuum:{...base.power.quantumVacuum,...patch.power?.quantumVacuum}},
  expression:{...base.expression,...patch.expression},
  learning:{...base.learning,...patch.learning},
  capabilities:{...base.capabilities,...patch.capabilities,attacks:{...base.capabilities.attacks,...patch.capabilities?.attacks}},
  links:{...base.links,...patch.links},
 }
}
export function characterDNAContributionsFor(fighterId:string){
 const canonicalId=normalizeFightHeroId(fighterId)
 return CHARACTER_DNA_CONTRIBUTIONS.filter(entry=>entry.fighterId===canonicalId).slice().sort((a,b)=>a.priority-b.priority||a.id.localeCompare(b.id))
}
function flattenContributionPatch(value:unknown,prefix='',out=new Map<string,string>()){
 if(value&&typeof value==='object'&&!Array.isArray(value)){
  for(const[key,item]of Object.entries(value as Record<string,unknown>)){
   const path=prefix?prefix+'.'+key:key
   flattenContributionPatch(item,path,out)
  }
 }else out.set(prefix,JSON.stringify(value))
 return out
}
export function characterDNAContributionConflicts(contributions:readonly CharacterDNAContribution[]){
 const seen=new Map<string,{id:string;value:string}>(),conflicts:Array<{fighterId:string;priority:number;path:string;first:string;second:string}>=[]
 for(const entry of contributions){
  for(const[path,value]of flattenContributionPatch(entry.patch)){
   const key=[entry.fighterId,entry.priority,path].join('|'),previous=seen.get(key)
   if(previous&&previous.value!==value)conflicts.push({fighterId:entry.fighterId,priority:entry.priority,path,first:previous.id,second:entry.id})
   else if(!previous)seen.set(key,{id:entry.id,value})
  }
 }
 return conflicts
}
export function composeCharacterDNA(base:CharacterDNA,contributions:readonly CharacterDNAContribution[]){
 const conflicts=characterDNAContributionConflicts(contributions)
 if(conflicts.length)throw new Error('Character DNA contribution conflict: '+conflicts.map(item=>item.fighterId+'@'+item.priority+':'+item.path+'['+item.first+' vs '+item.second+']').join(','))
 return contributions.reduce((current,entry)=>applyContribution(current,entry),base)
}

function canonicalVector(dna:Omit<CharacterDNA,'fingerprint'>|CharacterDNA){
 const flatten=(value:unknown):string=>{
  if(typeof value==='number')return round(value).toFixed(4)
  if(typeof value==='boolean')return value?'1':'0'
  if(typeof value==='string')return value
  if(value&&typeof value==='object')return Object.entries(value as Record<string,unknown>).filter(([key])=>key!=='fingerprint').sort(([a],[b])=>a.localeCompare(b)).map(([k,v])=>k+'='+flatten(v)).join(';')
  return''
 }
 return flatten(dna)
}
export function characterDNAFingerprint(dna:Omit<CharacterDNA,'fingerprint'>|CharacterDNA){
 const vector=canonicalVector(dna)
 return[0,1,2,3].map(i=>hash32(vector+'|'+i).toString(16).padStart(8,'0')).join('')
}
const cache=new Map<string,CharacterDNA>()
export function characterDNAForFighter(fighterId:string):CharacterDNA{
 const canonicalId=normalizeFightHeroId(fighterId)
 const existing=cache.get(canonicalId);if(existing)return existing
 const lineage=lifeFamilyFor(canonicalId),bodyPlan=bodyPlanFor(canonicalId,lineage),archetype=archetypeFor(canonicalId,bodyPlan),source=powerSourceFor(canonicalId,lineage)
 const base=baseline(canonicalId,lineage,bodyPlan,archetype,source)
 const composed=composeCharacterDNA(base,characterDNAContributionsFor(canonicalId))
 const unsigned={...composed,fingerprint:''}
 const dna={...unsigned,fingerprint:characterDNAFingerprint(unsigned)}
 cache.set(canonicalId,dna);return dna
}
export function characterDNADistance(a:CharacterDNA,b:CharacterDNA){
 const values=(value:unknown,out:number[]=[])=>{
  if(typeof value==='number')out.push(clamp(value))
  else if(typeof value==='boolean')out.push(value?1:0)
  else if(value&&typeof value==='object')for(const[key,item]of Object.entries(value as Record<string,unknown>).filter(([key])=>!['fighterId','fingerprint'].includes(key)).sort(([a],[b])=>a.localeCompare(b)))values(item,out)
  return out
 }
 const av=values(a),bv=values(b),length=Math.min(av.length,bv.length)
 if(!length)return 0
 let total=0
 for(let i=0;i<length;i++)total+=Math.abs(av[i]-bv[i])
 return total/length
}
export function characterDNAUniquenessReport(fighterIds:readonly string[]){
 const entries=fighterIds.map(id=>characterDNAForFighter(id)),duplicateFingerprints:string[]=[]
 const fingerprints=new Map<string,string>()
 for(const dna of entries){const existing=fingerprints.get(dna.fingerprint);if(existing)duplicateFingerprints.push(existing+'='+dna.fighterId);else fingerprints.set(dna.fingerprint,dna.fighterId)}
 let minimumDistance=1,nearestPair:[string,string]|null=null
 for(let i=0;i<entries.length;i++)for(let j=i+1;j<entries.length;j++){
  const distance=characterDNADistance(entries[i],entries[j])
  if(distance<minimumDistance){minimumDistance=distance;nearestPair=[entries[i].fighterId,entries[j].fighterId]}
 }
 return{count:entries.length,uniqueFingerprints:fingerprints.size,duplicateFingerprints,minimumDistance:entries.length>1?minimumDistance:1,nearestPair}
}
export function characterDNAProvenance(fighterId:string){
 const dna=characterDNAForFighter(fighterId),contributions=characterDNAContributionsFor(fighterId)
 return{
  fighterId,
  fingerprint:dna.fingerprint,
  contributions:contributions.map(entry=>({id:entry.id,source:entry.source,priority:entry.priority})),
  links:{...dna.links},
 }
}
export function characterDNACognitiveTraits(fighterId:string){return characterDNAForFighter(fighterId).mind}
export function characterDNAMotorBias(fighterId:string){
 const d=characterDNAForFighter(fighterId)
 return{
  confidence:clamp(.28+d.mind.courage*.28+d.mind.discipline*.2+d.physiology.structuralStability*.24),
  curiosity:clamp(d.mind.creativity*.55+d.learning.noveltyDrive*.45),
  focus:clamp(d.mind.precision*.26+d.mind.discipline*.22+d.senses.spatialSense*.16+d.power.control*.12+d.mind.architecture.workingMemory*.12+d.mind.architecture.selfModel*.12),
  aggression:clamp(d.mind.aggression*.5+d.combat.pressure*.3+d.expression.ferocity*.2),
  caution:clamp(d.mind.caution*.5+d.combat.retreatDiscipline*.24+d.mind.inhibition*.26),
  cadence:clamp(d.expression.cadence*.58+d.combat.tempo*.42),
  weight:clamp(d.expression.motionWeight*.46+d.physiology.mass*.34+d.physiology.structuralStability*.2),
 }
}
export function characterDNASkillBias(fighterId:string,kind:AttackKind){
 const d=characterDNAForFighter(fighterId),cap=d.capabilities.attacks[kind]
 if(kind==='light')return clamp(cap*(.42+d.combat.tempo*.3+d.mind.precision*.2+d.power.precision*.08))
 if(kind==='heavy')return clamp(cap*(.34+d.combat.pressure*.28+d.physiology.mass*.2+d.mind.aggression*.18))
 if(kind==='special')return clamp(cap*(.28+d.combat.specialBias*.24+d.power.amplitude*.22+d.power.control*.14+d.mind.creativity*.12))
 return clamp(cap*(.34+d.combat.throwBias*.28+d.mind.prediction*.2+d.physiology.reach*.18))
}
export function validateCharacterDNA(dna:CharacterDNA){
 const problems:string[]=[]
 if(!dna.fighterId)problems.push('missing fighterId')
 if(dna.mind.architecture.conscious!==true)problems.push('mind.architecture.conscious')
 if(!/^[0-9a-f]{32}$/.test(dna.fingerprint))problems.push('invalid fingerprint')
 const scan=(value:unknown,path:string)=>{
  if(typeof value==='number'&&(!Number.isFinite(value)||value<0||value>1))problems.push(path)
  else if(value&&typeof value==='object')for(const[key,item]of Object.entries(value as Record<string,unknown>))scan(item,path+'.'+key)
 }
 scan(dna.physiology,'physiology');scan(dna.senses,'senses');scan(dna.mind,'mind');scan(dna.combat,'combat');scan(dna.power,'power');scan(dna.expression,'expression');scan(dna.learning,'learning');scan(dna.capabilities.attacks,'capabilities.attacks')
 return{valid:problems.length===0,problems}
}
export function characterDNATelemetry(fighterId:string){
 const d=characterDNAForFighter(fighterId)
 return{fighterId:d.fighterId,fingerprint:d.fingerprint,lineage:d.lineage,bodyPlan:d.bodyPlan,archetype:d.archetype,powerSource:d.power.source,flight:d.capabilities.flight,quantum:d.capabilities.quantum,transformation:d.capabilities.transformation,links:d.links}
}

